/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ImageSize } from 'src/app/app-data/state/AbstractFileAttachApi';
import { Attachment } from '../../../../app-data/models/Attachment';
import { MatStatusIndicatorState } from '../../../../shared/ui/material/mat-status-indicator-state';
import { FileType } from '../../../../utils/file-utils';
import { AttachedFilesService } from '../../attached-files.service';
import { AttachmentsViewState, FileViewState } from '../../attachments-view-state';

export interface IFileSelection {
	file: Attachment;
	selected: boolean;
}

@Component({
	selector: 'attached-file-viewer',
	templateUrl: './attached-file-viewer.component.html',
	styleUrls: ['./attached-file-viewer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachedImageThumbnailComponent implements OnInit, OnDestroy {
	@Input() public file: Attachment;
	@Input() public size = ImageSize.Thumbnail;
	@Input() public showBorder = false;

	@Output() public fileClicked: EventEmitter<Attachment> = new EventEmitter();
	@Output() public fileSelected: EventEmitter<IFileSelection> = new EventEmitter();

	public fileType = FileType;
	public fileViewState = FileViewState;
	public spinnerState = MatStatusIndicatorState;
	public selected = new UntypedFormControl(false);
	public state$: BehaviorSubject<FileViewState>;

	public url: string;
	public containerDimensionClass: string;
	public imageDimensionClass: string;
	public matIconClass: string;

	private destroy$: Subject<void>;

	constructor(private attachedFilesService: AttachedFilesService) {}

	public ngOnInit(): void {
		this.containerDimensionClass = this.size === ImageSize.Thumbnail ? 'thumbnail' : 'preview';
		this.imageDimensionClass = this.size === ImageSize.Thumbnail ? 'thumbnail' : 'preview-aspect-ratio';
		this.matIconClass = this.size === ImageSize.Thumbnail ? 'mat-icon-thumbnail' : 'mat-icon-preview';

		// current state is derived from this.attachedFilesService.viewState & this.file.viewState
		// e.g. won't switch to default if it's file is currently in loading/error state when user switches to multi select mode
		this.state$ = new BehaviorSubject<FileViewState>(this.file.view.viewState);
		this.destroy$ = new Subject();
		this.url =
			this.size === ImageSize.Thumbnail ? this.file.view.thumbnailFileUrl : this.file.view.previewFileUrl;

		combineLatest([
			this.attachedFilesService.viewState$.pipe(
				startWith(this.file.view.viewState),
				tap(() => this.selected.patchValue(false))
			),
			this.attachedFilesService.api.renderTrigger$.pipe(
				startWith(this.file.$id),
				filter((id: string) => id === this.file.$id),
				tap(
					() =>
						(this.url =
							this.size === ImageSize.Thumbnail
								? this.file.view.thumbnailFileUrl
								: this.file.view.previewFileUrl)
				)
			),
		])
			.pipe(
				switchMap((combined) => this.changeViewState(combined[0] as AttachmentsViewState)),
				takeUntil(this.destroy$)
			)
			.subscribe(this.state$);
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public imageClickHandler(): void {
		if (this.state$.getValue() === FileViewState.Default) {
			this.fileClicked.emit(this.file);
		} else if (this.state$.getValue() === FileViewState.Selection) {
			this.selected.patchValue(!this.selected.value);
			this.fileSelected.emit({
				file: this.file,
				selected: this.selected.value,
			});
		}
	}

	private changeViewState(state: AttachmentsViewState): Observable<FileViewState> {
		let viewState: FileViewState = this.file.view.viewState;
		if (
			this.file.view.viewState !== FileViewState.Loading &&
			this.file.view.viewState !== FileViewState.Error
		) {
			viewState =
				state === AttachmentsViewState.MultiSelectMode ? FileViewState.Selection : FileViewState.Default;
		}
		return of(viewState);
	}
}
